'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef } from 'react';
import type { ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import { CLIENTS_DASHBOARD_QUERY_KEY } from '@/app/(main)/clients/const/clients';
import { useUser } from '@/context/UserProvider';
import { useUploadAndAttachMedia } from '@/hooks/useUploadAndAttachMedia';
import { getParsedError } from '@/utils/getParsedError';
import type { ErrorResponse } from '@/types/apiTypes';
import type { ImportClientType, ImportPrimaryClientsData } from '@/types/clientDetailsApiTypes';

export const CLIENTS_IMPORT_DEFAULT_ACCEPT = '.csv,text/csv';

export const CLIENTS_IMPORT_SPREADSHEET_ACCEPT =
  '.csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel';

interface ImportFileParams {
  file: File;
  agentId?: number;
  importSource?: string;
  importType?: ImportClientType;
}

type UseClientsFileImportOptions = {
  accept?: string;
};

export function useClientsFileImport({ accept = CLIENTS_IMPORT_DEFAULT_ACCEPT }: UseClientsFileImportOptions = {}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useUser();
  const queryClient = useQueryClient();
  const {
    uploadImportedClientsPrimaryPassengerFile,
    uploadImportedClientsRelatedFile,
    uploadImportedClientsLoyaltyProgramsFile,
    isBusy,
  } = useUploadAndAttachMedia();

  const importFile = useCallback(
    async ({ file, agentId, importSource, importType }: ImportFileParams): Promise<ImportPrimaryClientsData> => {
      const sharedMetadata = { importSource, importType };

      let response: Awaited<ReturnType<typeof uploadImportedClientsPrimaryPassengerFile>>;

      if (importType === 'related') {
        response = await uploadImportedClientsRelatedFile(file, undefined, sharedMetadata);
      } else if (importType === 'loyalty-programs') {
        response = await uploadImportedClientsLoyaltyProgramsFile(file, undefined, sharedMetadata);
      } else {
        response = await uploadImportedClientsPrimaryPassengerFile(file, undefined, {
          ...sharedMetadata,
          agentId,
        });
      }

      await queryClient.invalidateQueries({ queryKey: [CLIENTS_DASHBOARD_QUERY_KEY] });
      return response.data;
    },
    [
      queryClient,
      uploadImportedClientsPrimaryPassengerFile,
      uploadImportedClientsRelatedFile,
      uploadImportedClientsLoyaltyProgramsFile,
    ]
  );

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback(
    async (e: ChangeEvent<HTMLInputElement>) => {
      const input = e.target;
      const file = input.files?.[0];
      input.value = '';

      if (!file) {
        return;
      }

      if (!user) {
        toast.error('Sign in to upload a clients file.');
        return;
      }

      try {
        await uploadImportedClientsPrimaryPassengerFile(file);
        toast.success('Clients file uploaded successfully.');
        await queryClient.invalidateQueries({ queryKey: [CLIENTS_DASHBOARD_QUERY_KEY] });
      } catch (err) {
        const error = err as ErrorResponse<unknown>;
        toast.error(error.errors.details || getParsedError(err, error.status) || 'Upload failed');
      }
    },
    [queryClient, uploadImportedClientsPrimaryPassengerFile, user]
  );

  return {
    importFile,
    isBusy,
    fileInputRef,
    openFilePicker,
    handleFileChange,
    accept,
    isImporting: isBusy,
  };
}
